import { useState, useCallback, useRef, useEffect } from "react";
import { EmptyState } from "../ui/EmptyState";

interface BrowserWorkbenchProps {
  projectName: string;
}

function ensureScheme(raw: string): string {
  const trimmed = raw.trim();
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

export function BrowserWorkbench({ projectName }: BrowserWorkbenchProps) {
  const [url, setUrl] = useState("https://pages.opentool.io");
  const [loadedUrl, setLoadedUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Internal history stack for back/forward within the browser workbench
  const historyStack = useRef<string[]>([]);
  const historyIndex = useRef(-1);

  const navigateTo = useCallback((target: string, addToHistory = true) => {
    setError(null);
    setLoading(true);
    setLoadedUrl(target);
    setUrl(target);
    if (addToHistory) {
      // Truncate any forward history, then push
      historyStack.current = historyStack.current.slice(0, historyIndex.current + 1);
      historyStack.current.push(target);
      historyIndex.current = historyStack.current.length - 1;
    }
  }, []);

  const handleNavigate = useCallback(() => {
    const target = ensureScheme(url);
    navigateTo(target);
  }, [url, navigateTo]);

  const canGoBack = historyIndex.current > 0;
  const canGoForward = historyIndex.current < historyStack.current.length - 1;

  const handleBack = useCallback(() => {
    if (historyIndex.current > 0) {
      historyIndex.current -= 1;
      navigateTo(historyStack.current[historyIndex.current], false);
    }
  }, [navigateTo]);

  const handleForward = useCallback(() => {
    if (historyIndex.current < historyStack.current.length - 1) {
      historyIndex.current += 1;
      navigateTo(historyStack.current[historyIndex.current], false);
    }
  }, [navigateTo]);

  const handleIframeLoad = useCallback(() => {
    setLoading(false);
    // Check if the iframe loaded an error page from our proxy
    try {
      const iframe = iframeRef.current;
      if (iframe?.contentDocument) {
        const body = iframe.contentDocument.body?.textContent?.trim() ?? "";
        // Our proxy returns plain text error messages for failures
        if (body.startsWith("Failed to fetch page:") || body.startsWith("Upstream returned HTTP")) {
          setError(body);
        }
      }
    } catch {
      // Cross-origin — content loaded successfully from proxy (can't read it, which is fine)
    }
  }, []);

  // Listen for agent-driven navigation events
  useEffect(() => {
    const onAgentNavigate = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      const target = detail?.url;
      if (target && typeof target === "string") {
        navigateTo(ensureScheme(target));
      }
    };
    window.addEventListener("opentool-browser-navigate", onAgentNavigate);

    // Check for a pending navigation URL (set before this component mounted)
    const pending = (window as any).__opentool_pending_browser_url;
    if (pending && typeof pending === "string") {
      navigateTo(ensureScheme(pending));
      delete (window as any).__opentool_pending_browser_url;
    }

    return () => window.removeEventListener("opentool-browser-navigate", onAgentNavigate);
  }, [navigateTo]);

  const proxyUrl = loadedUrl
    ? `/api/browse?url=${encodeURIComponent(loadedUrl)}`
    : null;

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", gap: ".75rem" }}>
      {/* Address bar */}
      <div className="block" style={{ padding: ".5rem 1rem", display: "flex", alignItems: "center", gap: ".5rem", minHeight: "auto" }}>
        <button
          onClick={handleBack}
          disabled={!canGoBack}
          style={{
            background: "none", border: "none",
            color: canGoBack ? "var(--shell-muted)" : "var(--shell-line)",
            cursor: canGoBack ? "pointer" : "default",
            fontSize: ".8rem",
          }}
          title="Back"
        >
          ←
        </button>
        <button
          onClick={handleForward}
          disabled={!canGoForward}
          style={{
            background: "none", border: "none",
            color: canGoForward ? "var(--shell-muted)" : "var(--shell-line)",
            cursor: canGoForward ? "pointer" : "default",
            fontSize: ".8rem",
          }}
          title="Forward"
        >
          →
        </button>
        <button
          onClick={() => { if (loadedUrl) { setError(null); setLoading(true); setLoadedUrl(null); setTimeout(() => setLoadedUrl(loadedUrl), 0); } }}
          style={{ background: "none", border: "none", color: "var(--shell-muted)", cursor: "pointer", fontSize: ".8rem" }}
          title="Reload"
        >
          ↻
        </button>
        <input
          className="chat-input"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleNavigate()}
          placeholder="Enter a URL..."
          style={{
            flex: 1,
            background: "rgba(255,255,255,.03)",
            border: "1px solid var(--shell-line)",
            borderRadius: "6px",
            padding: ".35rem .6rem",
            color: "var(--shell-text)",
            fontSize: ".78rem",
            outline: "none",
          }}
        />
        <button
          onClick={handleNavigate}
          style={{
            background: "var(--shell-accent)",
            border: "none",
            borderRadius: "6px",
            padding: ".35rem .75rem",
            color: "var(--shell-bg)",
            fontSize: ".72rem",
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          Go
        </button>
        <button
          onClick={() => window.open(loadedUrl || url, "_blank", "noopener")}
          style={{ background: "none", border: "none", color: "var(--shell-muted)", cursor: "pointer", fontSize: ".72rem" }}
          title="Open in new tab"
        >
          ↗
        </button>
      </div>

      {/* Browser viewport */}
      <div className="block" style={{ flex: 1, position: "relative", overflow: "hidden" }}>
        {proxyUrl ? (
          <>
            {loading && (
              <div style={{
                position: "absolute", top: 0, left: 0, right: 0, height: "2px",
                background: "var(--shell-accent)", zIndex: 10,
                animation: "loading-bar 1.5s ease-in-out infinite",
              }}>
                <style>{`@keyframes loading-bar { 0% { width: 0%; } 50% { width: 70%; } 100% { width: 100%; opacity: 0; } }`}</style>
              </div>
            )}
            {error ? (
              <div style={{
                position: "absolute", inset: 0, display: "flex", flexDirection: "column",
                alignItems: "center", justifyContent: "center",
                background: "var(--shell-bg)", zIndex: 5,
              }}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--shell-muted)" strokeWidth="1.5" style={{ marginBottom: "1rem", opacity: 0.5 }}>
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 8v4M12 16h.01" />
                </svg>
                <p style={{ fontSize: ".85rem", color: "var(--shell-text)", marginBottom: ".35rem" }}>Could not load this page</p>
                <p style={{ fontSize: ".75rem", color: "var(--shell-muted)", maxWidth: "400px", textAlign: "center" }}>{error}</p>
                <button
                  onClick={() => { setError(null); setLoading(true); setLoadedUrl(null); setTimeout(() => setLoadedUrl(url), 0); }}
                  style={{
                    marginTop: "1rem", background: "var(--shell-accent)", border: "none",
                    borderRadius: "6px", padding: ".4rem 1rem", color: "var(--shell-bg)",
                    fontSize: ".75rem", fontWeight: 600, cursor: "pointer",
                  }}
                >
                  Try again
                </button>
              </div>
            ) : null}
            <iframe
              ref={iframeRef}
              src={proxyUrl}
              onLoad={handleIframeLoad}
              onError={() => { setLoading(false); setError("The page could not be reached. Check the URL and try again."); }}
              sandbox="allow-same-origin allow-scripts allow-forms allow-popups"
              style={{
                width: "100%",
                height: "100%",
                border: "none",
                background: "#fff",
                borderRadius: "4px",
              }}
            />
          </>
        ) : (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
            <EmptyState
              icon="M12 2a10 10 0 100 20 10 10 0 000-20z M2 12h20 M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10A15.3 15.3 0 0112 2z"
              title="Get started with Browser"
              subtitle="Enter a URL above and press Go to load a page."
            />
          </div>
        )}
      </div>
    </div>
  );
}
